/**
 * SKE-4681 — the three registers, in one place.
 *
 * Position constant, volume rising. The banner, the sidebar entry and later the
 * page all read their treatment from here, so a register cannot escalate on one
 * surface and stay calm on another.
 *
 * THE TOKEN TRANSLATION. The campaign spec is written in Sked MARKETING-SITE
 * tokens and this app's palette is not the same one. `--surface-peach`,
 * `--shadow-cta`, `--shadow-card` and the spec's blue-grey neutral ramp do not
 * exist here, so the intent is translated onto the app's existing Tailwind
 * palette and nothing is added to the token set.
 *
 * ONE VALUE DELIBERATELY DIFFERS FROM THE SPEC, and it is a correction rather
 * than a preference. The spec's week 3 ground is its own red-500 `#D92D20`,
 * which carries white text at 4.83:1. This app's `--color-red-500` is
 * `#ef4444`, and white on that is **3.76:1, which fails AA for body text**.
 * The app's `--color-red-600` is `#c83f2d`, and white on that is **4.99:1**.
 * So week 3 is red-600. Same intent, a solid red that reads as a change from
 * across the room, with the contrast the spec itself argued for.
 *
 * The spice ramp needs no such correction: `--color-spice-500` and
 * `--color-spice-700` are byte-identical to the spec's. **Spice-500 is the
 * ACCENT and spice-700 is the INK**: spice-500 on the spice-50 ground is
 * 2.24:1 and must never carry type, spice-700 is 4.66:1 and passes.
 *
 * Where the ink actually lands, which is NOT what an earlier version of this
 * comment claimed: line one, the countdown included, renders through
 * `title`, which is `text-neutral-900` in weeks 1 and 2. That is 16.08:1 on
 * the spice ground, better than spice-700 rather than worse, and it is the
 * right call for the sentence a customer has to read. `body` (spice-700) is
 * the supporting line: the progress label. So the spec's "countdown in
 * spice-700" is honoured in the sense that matters, which is that spice-500
 * never carries type anywhere.
 *
 * COPY. Week 1 is verbatim from the SKE-4681 table. Weeks 2 and 3 are the
 * calmer lines Hugh approved on 2026-10-16 (SKE-5358), changed only where a
 * count of one needs the singular or a missing date would leave a hole.
 *
 * THE DATE IS NEVER NUMERIC. The cutover renders as a named weekday, day and
 * month, in the CUSTOMER'S OWN TIMEZONE, which is why the server deliberately
 * sends no display strings and the formatting happens here. The customer base
 * spans day-first and month-first conventions, so "30/10" is ambiguous to half
 * of them and a named month is not.
 *
 * THE TIMEZONE IS THE PROFILE'S, NEVER THE BROWSER'S. It arrives as
 * `context.timeZone`, which callers take from `user.timezone` on the redux
 * slice, the same source `utils/dates.ts` uses for every other date in the
 * product. A browser-zone date would tell an agency in Auckland a different
 * deadline than the account owner in Los Angeles reads, and would move the
 * deadline under a customer who opens Sked on a laptop still set to the zone of
 * last week's holiday. Near a midnight boundary the two differ by a whole
 * calendar day, which on the campaign's loudest surface is a wrong deadline.
 *
 * The day count appears in the final 72 hours only, and always ALONGSIDE the
 * date rather than instead of it, so the date stays copyable into a calendar.
 * The account count in week 3 is the OUTSTANDING count, not the total: it is
 * how many accounts will stop publishing.
 *
 * The vocabulary rules, which no string here may break: the verb is UPDATE and
 * the button is "Update now". The word "reconnect" is banned from every
 * customer-facing string. The controls that forced this are now "Update phone
 * login" and "Update Facebook permissions"; before that, "Reconnect Account" meant the phone
 * login elsewhere in the product. Never say migration, scope, permission set,
 * Content Publishing API, token, or "the composer"; say "when you create a
 * post". The lead angle is better publishing, never security or passwords.
 */

/**
 * The campaign page's route.
 *
 * It lives HERE rather than on the banner, where it started, because the page
 * itself needs it and importing the banner for a string would pull the banner,
 * the banner-stack precedence store and the analytics client into the page's
 * lazy chunk. This file is the one every campaign surface already imports.
 */
export const INSTAGRAM_CAMPAIGN_ROUTE = "/dashboard/instagram-publishing";

export type CampaignWeek = 1 | 2 | 3;

/**
 * SKE-4688 — which capability the campaign leads with.
 *
 * `"audio"` is the decision, taken on 2026-09-11 and reaffirmed: the hero
 * shows the trending-audio picker because it is the more interesting moment
 * for a customer. `"reel_cover"` is the insurance. The one live test published
 * showing "Original audio" rather than the chosen track, which the unpublished
 * Meta app may or may not explain, and if the live check after production
 * still reads that way then the campaign's largest visual is promising a
 * capability that does not work.
 *
 * THE WHOLE POINT IS THAT FLIPPING IT IS ONE LINE. It moves THREE things
 * together, and `hero-variant-switch.test.tsx` fails if any one of them stops
 * moving:
 *
 *   1. the hero mock, audio picker to reel-cover filmstrip,
 *   2. the middle capability card, trending audio to reel covers,
 *   3. the "trending audio" phrase in the per-account completion sentence.
 *
 * A hero-only switch would leave a completion sentence promising a capability
 * the page no longer shows anywhere, which is the failure this constant exists
 * to make impossible.
 *
 * It is a CONSTANT, not a feature toggle. There is no per-workspace answer
 * here: either the capability works for everyone or it is off the page, and a
 * toggle would invite the campaign's hero to differ between two customers
 * looking at it side by side. Flipping it is a one-line edit and a deploy.
 */
export type HeroVariant = "audio" | "reel_cover";

export const HERO_VARIANT: HeroVariant = "audio";

/**
 * The cutover as a named weekday, day and month, in the customer's own timezone
 * and their own locale.
 *
 * Locale-driven rather than hard-coded to "Thursday 30 October", because the
 * REASON the ticket gives for banning a numeric date is that day-first and
 * month-first customers read the same digits differently. A named month is
 * unambiguous in either order, so an American customer reading "Thursday,
 * October 30" is served by the rule rather than excepted from it.
 *
 * Returns an empty string when there is no date. Every caller composes this
 * into a sentence, so a formatter that threw would take the banner down with
 * it, and no date is worth that.
 *
 * `timeZone` is the customer's profile zone. An unknown or malformed zone makes
 * `Intl` throw, so that case falls back to the browser's zone and still renders
 * a date: a date that is a day out beats a banner with a hole in its sentence,
 * and the profile zone comes from a select the customer cannot type into.
 */
export function formatCutover(
  cutoverAt: string | null | undefined,
  timeZone?: string | null
): string {
  if (!cutoverAt) return "";
  const date = new Date(cutoverAt);
  if (Number.isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
  };
  try {
    return new Intl.DateTimeFormat(undefined, {
      ...options,
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch {
    // Only an unusable `timeZone` reaches here; retry without it.
    try {
      return new Intl.DateTimeFormat(undefined, options).format(date);
    } catch {
      return "";
    }
  }
}

/**
 * "1 day" or "3 days", never "1 days".
 *
 * The number comes from the server and is not recomputed here; only the plural
 * is decided locally. That is the one liberty taken with the verbatim copy, on
 * the precedent of `subscription-banner.tsx`, which does exactly this for its
 * trial countdown. "1 days left" in a week 3 banner is a defect a customer
 * notices in the register that can least afford one.
 */
export function dayCount(days: number | null | undefined): string {
  const value = typeof days === "number" && days > 0 ? days : 0;
  return `${value} ${value === 1 ? "day" : "days"}`;
}

/** "1 account" or "4 accounts", for the same reason. */
export function accountCount(count: number): string {
  return `${count} ${count === 1 ? "account" : "accounts"}`;
}

/** What line one needs in order to render its counts and date. */
export interface CampaignCopyContext {
  /** ISO 8601 from the server, or null when no date is configured. */
  cutoverAt: string | null | undefined;
  /** Whole days remaining, from the server. Never recomputed here. */
  daysRemaining: number | null | undefined;
  /** Accounts still to update. NOT the total. */
  outstanding: number;
  /**
   * The customer's profile timezone (`user.timezone`), NOT the browser's.
   * Optional only so a caller without a loaded user still renders a date.
   */
  timeZone?: string | null;
  /** From the server. Week 3 swaps its subhead inside the final 72 hours. */
  isFinal72Hours?: boolean;
}

/**
 * Week 3's subhead. Inside the final 72 hours it leads with the day count,
 * which the server keeps at 1 or more there. A missing or zero count drops
 * that sentence rather than saying "0 days left".
 */
function week3Subhead({
  daysRemaining,
  outstanding,
  isFinal72Hours,
}: CampaignCopyContext): string {
  if (!isFinal72Hours) {
    return "Updated accounts are unaffected. Each update takes about a minute.";
  }
  const action =
    outstanding === 1
      ? "Update it now. It takes about a minute."
      : "Update them now, about a minute each.";
  return typeof daysRemaining === "number" && daysRemaining > 0
    ? `${dayCount(daysRemaining)} left. ${action}`
    : action;
}

export interface CampaignRegister {
  /** Line one, with its counts and date filled in. */
  message: (context: CampaignCopyContext) => string;
  /** The primary button. */
  buttonLabel: string;
  /** Week 3 only: the flag that replaces the eyebrow. */
  flag?: string;
  /** A quieter line under line one, in `body` ink. */
  subhead?: string | ((context: CampaignCopyContext) => string);
  /** Tailwind classes for the banner ground. */
  wrapper: string;
  /** Ink for line one. */
  title: string;
  /** Ink for the supporting line and the countdown. */
  body: string;
  /** The cva `Button` variant this register's primary button uses. */
  buttonVariant: "blue-primary" | "red-primary" | "white";
  /** The filled portion of the progress bar. */
  progressFill: string;
  /** The unfilled track. */
  progressTrack: string;
  /**
   * Ink for the dismiss control, and its FOCUS RING.
   *
   * The ring is not decoration. `public/styles/button.less` sets
   * `button { outline: none }` on every bare button in the app, so a dismiss
   * control with only a hover style is unreachable-looking to anyone using a
   * keyboard, which fails WCAG 2.4.7. A ring is a box-shadow and the legacy
   * reset cannot suppress it. The shared `Button` used by the call to action
   * already carries one; this is the control that does not.
   */
  dismiss: string;
  /** The sidebar entry's label at this register. */
  menuLabel: string;
  /** Whether the sidebar entry takes the red treatment. */
  menuIsUrgent: boolean;
}

export const CAMPAIGN_REGISTERS: Record<CampaignWeek, CampaignRegister> = {
  1: {
    message: () =>
      "Instagram publishing is getting an upgrade. Update your accounts to switch over.",
    subhead: "Publishing works as usual in the meantime, updated or not.",
    buttonLabel: "See what's new",
    wrapper: "bg-spice-50 border-spice-200",
    title: "text-neutral-900",
    body: "text-spice-700",
    buttonVariant: "blue-primary",
    progressFill: "bg-spice-700",
    progressTrack: "bg-spice-200 ring-1 ring-inset ring-spice-700/40",
    dismiss:
      "text-spice-700 hover:bg-spice-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-spice-700 focus-visible:ring-offset-2 focus-visible:ring-offset-spice-50",
    menuLabel: "Update Instagram",
    menuIsUrgent: false,
  },
  2: {
    message: ({ cutoverAt, timeZone }) => {
      const cutover = formatCutover(cutoverAt, timeZone);
      return cutover
        ? `Update your Instagram accounts in Sked Social by ${cutover}.`
        : "Update your Instagram accounts in Sked Social.";
    },
    subhead: ({ cutoverAt, timeZone }) =>
      formatCutover(cutoverAt, timeZone)
        ? "About a minute each. Accounts not updated by then stop publishing."
        : "About a minute each. Accounts not updated stop publishing.",
    buttonLabel: "Update now",
    wrapper: "bg-spice-50 border-spice-200",
    title: "text-neutral-900",
    // spice-700, never spice-500. See the note at the top of this file.
    body: "text-spice-700",
    buttonVariant: "blue-primary",
    progressFill: "bg-spice-700",
    progressTrack: "bg-spice-200 ring-1 ring-inset ring-spice-700/40",
    dismiss:
      "text-spice-700 hover:bg-spice-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-spice-700 focus-visible:ring-offset-2 focus-visible:ring-offset-spice-50",
    menuLabel: "Update Instagram",
    menuIsUrgent: false,
  },
  3: {
    message: ({ cutoverAt, outstanding, timeZone }) => {
      const cutover = formatCutover(cutoverAt, timeZone);
      const verbs =
        outstanding === 1
          ? "still needs updating or stops publishing"
          : "still need updating or stop publishing";
      return cutover
        ? `${accountCount(outstanding)} ${verbs} on ${cutover}.`
        : `${accountCount(outstanding)} ${verbs}.`;
    },
    subhead: week3Subhead,
    buttonLabel: "Update now",
    flag: "Action required",
    // red-600, not red-500. White on red-500 fails AA here.
    wrapper: "bg-red-600 border-red-600",
    title: "text-white",
    body: "text-white",
    buttonVariant: "white",
    progressFill: "bg-white",
    progressTrack: "bg-white/30 ring-1 ring-inset ring-white/70",
    dismiss:
      "text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-red-600",
    menuLabel: "Action required",
    menuIsUrgent: true,
  },
};

export function registerFor(week: number | null | undefined): CampaignRegister {
  if (week === 3) return CAMPAIGN_REGISTERS[3];
  if (week === 2) return CAMPAIGN_REGISTERS[2];
  return CAMPAIGN_REGISTERS[1];
}
